import { defineField, defineType } from 'sanity'

import getPageInfo, { seoGroup } from '../objects/pageInfo'

export default defineType({
  name: 'accessory',
  title: 'Accessories',
  type: 'document',
  orderings: [
    {
      title: 'Name A-Z',
      name: 'nameAsc',
      by: [{field: 'name', direction: 'asc'}],
    },
  ],
  groups: [
    {
      name: 'content',
      title: 'Content',
      default: true,
    },
    {
      name: 'config',
      title: 'Config',
      default: false,
    },
    seoGroup,
  ],
  fields: [
    defineField({
      name: 'order',
      title: 'Order',
      type: 'number',
      group: 'config',
    }),
    defineField({
      name: 'name',
      title: 'Name',
      type: 'string',
      description: 'H1 title on the page',
      validation: (rule) => rule.required(),
      group: 'content',
    }),
    defineField({
      title: 'Hero',
      name: 'hero',
      type: 'hero',
      group: 'content',
    }),
    defineField({
      name: 'photo',
      title: 'Photo',
      type: 'image',
      options: {
        hotspot: true,
      },
      group: 'content',
    }),
    defineField({
      name: 'configuration',
      title: 'Configuration',
      type: 'reference',
      to: [
        {
          type: 'accessoryConfiguration',
        },
      ],
      group: 'content',
    }),
    defineField({
      name: 'bundleProducts',
      title: 'Bundle Products',
      description: 'Optional: Select accessory bundles to display them seamlessly among regular products',
      type: 'array',
      of: [
        {
          type: 'reference',
          to: [{ type: 'accessoryBundle' }],
        },
      ],
      validation: (rule) => rule.custom(async (bundleProducts, context) => {
        // Skip validation if no bundles selected
        if (!bundleProducts || bundleProducts.length === 0) return true

        // Get all bundle IDs
        const bundleIds = (bundleProducts as Array<{_ref?: string}>)
          .filter((ref): ref is {_ref: string} => Boolean(ref._ref))
          .map((ref) => ref._ref)

        if (bundleIds.length === 0) return true

        // Check if getClient is available (Sanity v4)
        const client = context.getClient?.({apiVersion: '2024-01-01'})
        if (!client) {
          // If client is not available, skip validation
          return true
        }

        // Fetch all bundles at once
        const bundles = await client.fetch<Array<{_id: string; name?: string; bundleName?: string; slug?: {current?: string}}>>(
          `*[_id in $ids]{_id, name, bundleName, slug}`,
          { ids: bundleIds }
        )

        // Check for bundles without slugs
        const bundlesWithoutSlugs = bundles.filter((bundle) => !bundle?.slug?.current)

        if (bundlesWithoutSlugs.length > 0) {
          const names = bundlesWithoutSlugs.map((b) => b.name || b._id).join(', ')
          return `The following bundles need a URL slug before they can be added: ${names}`
        }

        return true
      }),
      group: 'content',
    }),
    getPageInfo({ path: 'accessory' }),
  ],
  preview: {
    select: {
      title: 'name',
      media: 'photo',
    },
    prepare({ title, media }) {
      return {
        title,
        subtitle: 'Accessory',
        media,
      }
    },
  },
})